/**
 * The public viewing room's controller: `room.model.ts` wired to
 * `fetchRoomGate` for the cover and `enterRoom` for the contents.
 *
 * It owns what the model cannot: the cover and contents as they arrive, the
 * two typed fields, the email the visitor has already GIVEN (which is what
 * advances the ladder from one prompt to the next), the refused and busy
 * flags, and the guard that stops a signed-in member's automatic entry firing
 * twice. The model decides what the room still requires and how it reads.
 *
 * This is the one controller that must work with no session. It reads
 * `session` and `loading` from the account and never `me`, because `useMe`
 * spins for ever without a user id — see the note on the hook.
 */

import type { RoomGateResponse, RoomResponse } from "@ondo/shared";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAccount } from "../lib/account.ts";
import { enterRoom, fetchRoomGate } from "../lib/api.ts";
import { ROOM_COPY } from "./room.constants.ts";
import {
  describeEntryError,
  isMissing,
  isTerminal,
  looksLikeEmail,
  outstandingField,
  parseRoomAddress,
  requiredFields,
  resolveRoom,
} from "./room.model.ts";
import type { RoomView, Viewer } from "./room.model.ts";

export interface RoomController {
  view: RoomView;
  typeEmail: (value: string) => void;
  typePassword: (value: string) => void;
  enter: () => void;
  openWork: (workId: string) => void;
  closeWork: () => void;
}

/**
 * A viewing room, for whoever opened the link.
 *
 * The one controller in the app that must work with no session at all, which
 * makes two things load-bearing:
 *
 *   * `me` is NEVER read. `useMe` returns `{ status: "loading" }` for ever when
 *     there is no user id — the effect returns early — so a public screen that
 *     branched on it would spin at exactly the people this page exists for.
 *     Only `session` and `loading` are taken, and both resolve for a signed-out
 *     caller.
 *   * nothing here touches `supabase` directly. Image URLs arrive pre-signed
 *     from the API and the chain arrives inline, because a visitor cannot sign a
 *     storage URL and cannot read `ondo.work_images` at all.
 */
export function useRoomController(
  handle: string | undefined,
  slug: string | undefined,
): RoomController {
  // Session only. See the docblock — reading `me` here is the bug this comment
  // exists to prevent, and there is a component test for it.
  const { session, loading } = useAccount();

  const [cover, setCover] = useState<RoomGateResponse | null>(null);
  const [coverFailed, setCoverFailed] = useState(false);
  const [contents, setContents] = useState<RoomResponse | null>(null);
  const [email, setEmail] = useState("");
  /**
   * The address the visitor has already given, as opposed to what is in the box.
   *
   * This is what advances the ladder from the email step to the password one.
   * Without it "what the room requires" and "what is still outstanding" are the
   * same question, and a room needing both can never be opened.
   */
  const [givenEmail, setGivenEmail] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openWorkId, setOpenWorkId] = useState<string | null>(null);

  const address = parseRoomAddress(handle, slug);
  const key = address ? `${address.handle}:${address.slug}` : null;
  const signedIn = session !== null;

  // Guards the automatic entry below against React's double-invoke in
  // StrictMode. Without it a signed-in member at an open room would file two
  // visits before the page had finished its first render.
  const entered = useRef<string | null>(null);

  const viewer: Viewer = { signedIn, email: session?.user.email ?? null };

  // Keyed on the raw route params rather than on the parsed address: parsing
  // returns a fresh object every render, and an effect depending on one would
  // re-fetch on each.
  useEffect(() => {
    const target = parseRoomAddress(handle, slug);
    if (!target || loading) return;
    let active = true;

    fetchRoomGate(target.handle, target.slug)
      .then((result) => {
        if (active) setCover(result);
      })
      // Unknown, hidden and deleted are one answer, and so is a network
      // failure as far as this page is concerned: there is nothing to show and
      // nothing useful to say about why.
      .catch(() => {
        if (active) setCoverFailed(true);
      });

    return () => {
      active = false;
    };
  }, [handle, slug, loading]);

  const go = useCallback(
    (body: { email?: string | null; password?: string | null }) => {
      const target = parseRoomAddress(handle, slug);
      if (!target) return;

      setBusy(true);
      setProblem(null);

      enterRoom(target.handle, target.slug, body)
        .then((result) => {
          setContents(result);
          // The password is not kept after it has been spent. Nothing reads it
          // again, and a form that still held it would put it in a re-render.
          setPassword("");
        })
        .catch((error: unknown) => {
          if (isMissing(error)) {
            setCoverFailed(true);
            return;
          }
          // A wrong password re-offers the field with a message; a room that has
          // stopped answering does not, because re-showing it would be a lie
          // about what typing it again would do.
          if (isTerminal(error)) {
            setRefused(true);
            return;
          }
          setProblem(describeEntryError(error));
        })
        .finally(() => setBusy(false));
    },
    [handle, slug],
  );

  // A member at an open room is asked for nothing, so they are taken straight
  // in rather than shown a button whose only purpose is to be pressed.
  useEffect(() => {
    if (!cover || !key || contents || refused) return;
    if (requiredFields(cover, { signedIn, email: null }).length > 0) return;
    if (entered.current === key) return;

    entered.current = key;
    go({});
  }, [cover, key, contents, refused, signedIn, go]);

  return {
    view: resolveRoom({
      address,
      cover,
      coverFailed,
      contents,
      viewer,
      email,
      givenEmail,
      password,
      problem,
      refused,
      busy,
      openWorkId,
    }),

    typeEmail: (value) => {
      setEmail(value);
      setProblem(null);
    },

    typePassword: (value) => {
      setPassword(value);
      setProblem(null);
    },

    /**
     * Answers whatever the room is currently asking for.
     *
     * The email step COMPLETES LOCALLY rather than being sent on its own. It
     * used to post immediately, which meant a room wanting both an email and a
     * password was unopenable: the request went with `password: null`, the
     * server refused, and "That password is not right" appeared above an email
     * box with nowhere to type one. Nothing is sent until nothing is
     * outstanding.
     */
    enter: () => {
      if (!cover) return;

      const outstanding = outstandingField(cover, viewer, { email: givenEmail });

      if (outstanding === "email") {
        const typed = email.trim();
        if (!looksLikeEmail(typed)) {
          setProblem(ROOM_COPY.emailRequired);
          return;
        }

        setGivenEmail(typed);
        // Nothing else to ask for, so go straight in rather than making them
        // press a second button that could only say the same thing.
        if (!cover.requiresPassword) go({ email: typed });
        return;
      }

      go({ email: givenEmail, password: password === "" ? null : password });
    },

    openWork: setOpenWorkId,
    closeWork: () => setOpenWorkId(null),
  };
}
